import { useCallback, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AccessControlContext } from "../../contexts/AccessControlContext";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

import { Card } from "../../shared/Card";
import { FormContainer } from "../../shared/FormLayout/FormContainer";
import { FormRow } from "../../shared/FormLayout/FormRow";
import { ProgressButton } from "../../shared/ProgressButton";

import { WorkOrderAssignmentSubmission, WorkOrderWithAssignment } from "../../shared/interfaces/WorkOrder";

import { useAssignWorkOrder } from "../../data/Assignment/useAssignWorkorder";
import { useReassignWorkOrder } from "../../data/Assignment/useReassignWorkorder";
import { useUnassignWorkOrder } from "../../data/Assignment/useUnassignWorkorder";

import { UserDropdown } from "../../UserDropdown";

import { CancelButton } from "../../shared/CancelButton";
import { Statuses } from "../../shared/Enums";
import { ValidationMessage } from "../../shared/ValidationMessage";

interface WorkorderAssignmentProps {
  workorder: WorkOrderWithAssignment;
  isLoading: boolean;
  editingWorkorder: boolean;
  editingAssignment: boolean;
  onEditingAssignmentModeChange: (editingAssignment: boolean) => void;
  onAssignmentUpdateSuccess: () => void;
  onAssignmentUpdateError: () => void;
  onAssignmentUpdateLoading: () => void;
}

export default function WorkorderAssignment(props: WorkorderAssignmentProps) {
  const { t } = useTranslation();
  const accessControlContext = useContext(AccessControlContext);

  const {
    workorder,
    isLoading,
    editingWorkorder,
    editingAssignment,
    onEditingAssignmentModeChange,
    onAssignmentUpdateSuccess,
    onAssignmentUpdateError,
    onAssignmentUpdateLoading,
  } = props;
  const {
    formState: { errors },
    setValue,
    handleSubmit,
    control,
    clearErrors,
  } = useForm({ mode: "onBlur" }); // retrieve all hook methods

  const userCanEditAssignment = workorder && accessControlContext.canI("edit", "assignment", workorder.customerId);
  const userCanAddAssignment = workorder && accessControlContext.canI("add", "assignment", workorder.customerId);
  const userCanEditWorkorder = workorder && accessControlContext.canI("edit", "workorder", workorder.customerId);

  const [assignedUserId, setAssignedUserId] = useState<string | undefined | null>(workorder?.assignment?.deviceUserId);

  const {
    mutate: reassignWorkorder,
    isLoading: reassigningWorkorderLoading,
    reset: resetReassignWorkOrderStatus,
  } = useReassignWorkOrder({
    onSuccess: handleAssignmentChangeSuccess,
    onError: onAssignmentUpdateError,
  });

  const {
    mutate: assignWorkorder,
    isLoading: workorderAssignmentLoading,
    reset: resetAssignWorkOrderStatus,
  } = useAssignWorkOrder({
    onSuccess: handleAssignmentChangeSuccess,
    onError: onAssignmentUpdateError,
  });

  const { mutate: unassignWorkorder, isLoading: workorderUnassignmentLoading } = useUnassignWorkOrder({
    onSuccess: handleAssignmentChangeSuccess,
    onError: onAssignmentUpdateError,
  });

  function handleAssignedUserSelectionChange(deviceUserId: string) {
    setAssignedUserId(deviceUserId);
    setValue("selectedUserId", deviceUserId, { shouldValidate: true });
  }

  useEffect(() => {
    if (reassigningWorkorderLoading || workorderAssignmentLoading || workorderUnassignmentLoading) {
      onAssignmentUpdateLoading();
    }
  }, [
    reassigningWorkorderLoading,
    workorderAssignmentLoading,
    workorderUnassignmentLoading,
    onAssignmentUpdateLoading,
  ]);

  function handleAssignmentChangeSuccess() {
    onEditingAssignmentModeChange(false);

    resetReassignWorkOrderStatus();
    resetAssignWorkOrderStatus();
    onAssignmentUpdateSuccess();
  }

  const saveAssignmentUpdates = handleSubmit(() => {
    // Check if assignment needs to be updated
    if (
      workorder &&
      assignedUserId &&
      workorder.assignment?.deviceUserId &&
      workorder.assignment.deviceUserId !== assignedUserId
    ) {
      const assignmentSubmission: WorkOrderAssignmentSubmission = {
        workOrderId: workorder.id,
        deviceUserId: assignedUserId,
      };
      reassignWorkorder(assignmentSubmission);
    }
    // Check if work order was not assigned and a new assignment has been made
    else if (
      workorder &&
      assignedUserId &&
      (workorder.assignment?.deviceUserId == null || workorder.assignment.deviceUserId === "")
    ) {
      const assignmentSubmission: WorkOrderAssignmentSubmission = {
        workOrderId: workorder.id,
        deviceUserId: assignedUserId,
      };
      assignWorkorder(assignmentSubmission);
    }
  });

  const handleUnassignment = useCallback(() => {
    editingAssignment && userCanEditWorkorder && workorder && unassignWorkorder(workorder.id);
    setAssignedUserId(undefined);
    clearErrors();
  }, [editingAssignment, unassignWorkorder, userCanEditWorkorder, workorder, clearErrors]);

  function handleCancelAssignmentClick() {
    setAssignedUserId(workorder?.assignment?.deviceUserId);
    onEditingAssignmentModeChange(false);
    clearErrors();
  }

  return (
    <Card title={t("workorders.assignment")} className="mb-3" loading={isLoading}>
      <FormContainer>
        {editingAssignment ? (
          <FormRow
            label={t("workorders.assignedUsername")}
            value={
              workorder && userCanEditAssignment && userCanAddAssignment ? (
                <div>
                  <Controller
                    control={control}
                    name="selectedUserId"
                    defaultValue={assignedUserId}
                    rules={{
                      required: true,
                      validate: {
                        isChanged: (value) => value !== workorder?.assignment?.deviceUserId,
                      },
                    }}
                    render={({ field: { onBlur } }) => (
                      <UserDropdown
                        selectedDeviceUserId={assignedUserId}
                        customerId={workorder.customerId}
                        assetId={workorder?.assetIds?.[0] ?? workorder.assetId}
                        onAssignedUserChange={handleAssignedUserSelectionChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  {errors?.selectedUserId?.message && (
                    <ValidationMessage invalidMessage={errors?.name?.message} validationType="error" showIcon />
                  )}
                  {errors?.selectedUserId?.type === "isChanged" && (
                    <ValidationMessage
                      invalidMessage={t("workorders.workorderAssignmentNotUpdated")}
                      validationType="error"
                      showIcon
                    />
                  )}
                </div>
              ) : (
                (workorder?.assignment?.userName ?? t("workorders.notAssigned"))
              )
            }
          />
        ) : (
          <>
            <FormRow
              label={t("workorders.assignedUser")}
              value={
                workorder?.assignment?.emailAddress ? workorder.assignment.emailAddress : t("workorders.notAssigned")
              }
            />
            <FormRow
              label={t("workorders.assignedUsername")}
              value={workorder?.assignment?.userName ? workorder?.assignment.userName : t("workorders.notAssigned")}
            />
            <FormRow
              label={t("workorders.assignedDate")}
              value={
                workorder?.assignment?.statusDate
                  ? t("common.formattedDate", {
                      dateString: workorder?.assignment?.statusDate,
                    })
                  : ""
              }
            />
          </>
        )}
      </FormContainer>
      <br />
      <Box display="flex" justifyContent="flex-end">
        {userCanEditAssignment && !editingWorkorder && (
          <>
            {editingAssignment && (
              <div>
                <ProgressButton
                  startIcon={<SaveIcon />}
                  onClick={saveAssignmentUpdates}
                  disabled={reassigningWorkorderLoading || workorderAssignmentLoading}
                  loading={reassigningWorkorderLoading || workorderAssignmentLoading}
                >
                  {t("workorders.assign")}
                </ProgressButton>
                {workorder.assignment?.deviceUserId && (
                  <ProgressButton
                    color="secondary"
                    onClick={handleUnassignment}
                    disabled={workorderUnassignmentLoading}
                    loading={workorderUnassignmentLoading}
                  >
                    {" "}
                    {t("workorders.unassign")}{" "}
                  </ProgressButton>
                )}
                <CancelButton onClick={handleCancelAssignmentClick} className="m1-1" />
              </div>
            )}

            {!editingAssignment &&
              workorder?.status !== Statuses.COMPLETE &&
              workorder?.status !== Statuses.CANCELLED && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={() => onEditingAssignmentModeChange(true)}
                >
                  {t("workorders.editAssignment")}{" "}
                </Button>
              )}
          </>
        )}
      </Box>
    </Card>
  );
}
